import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

// "Everything that paints asynchronously has painted" for the print route.
//
// Maps take their snapshot after the tiles load; the print dialog must not open
// before that. Each such component holds the page while it works and releases
// it when done; the provider fires onReady once nothing is held any more (after
// a short settle, so a component mounting a beat later still counts), and in any
// case after a hard cap so one stuck tile never blocks the PDF.
const Ctx = createContext(null);

export function PrintReadyProvider({ children, onReady, settleMs = 800, capMs = 15000 }) {
  const held    = useRef(0);
  const timer   = useRef(null);
  const fired   = useRef(false);
  const readyRef = useRef(onReady);
  useEffect(() => { readyRef.current = onReady; });

  const fire = useCallback(() => {
    if (fired.current) return;
    fired.current = true;
    clearTimeout(timer.current);
    readyRef.current?.();
  }, []);

  const value = useMemo(() => ({
    hold() { held.current += 1; clearTimeout(timer.current); },
    release() {
      held.current = Math.max(0, held.current - 1);
      if (held.current === 0) { clearTimeout(timer.current); timer.current = setTimeout(fire, settleMs); }
    },
  }), [fire, settleMs]);

  useEffect(() => {
    // No holder at all (a report without maps) → ready after the settle window.
    timer.current = setTimeout(fire, settleMs);
    const cap = setTimeout(fire, capMs);
    return () => { clearTimeout(timer.current); clearTimeout(cap); };
  }, [fire, settleMs, capMs]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// Hold the page while `active`; returns a stable release(). Outside a provider
// (the normal on-screen report) it is a no-op.
export function usePrintHold(active) {
  const ctx = useContext(Ctx);
  const released = useRef(false);
  useEffect(() => {
    if (!active || !ctx) return undefined;
    released.current = false;
    ctx.hold();
    return () => { if (!released.current) { released.current = true; ctx.release(); } };
  }, [active, ctx]);
  return useCallback(() => {
    if (!active || !ctx || released.current) return;
    released.current = true;
    ctx.release();
  }, [active, ctx]);
}
